/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-filename-extension */
import React, { FC, useContext, useRef, useState } from 'react';
import { I18n, ColumnChooserCategory } from '@bingads-webui-grid/well-known';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { DefaultButton, Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { Columns } from '@bingads-webui-component-legacy/bingads-grid/service/column-chooser-preferences';
import _ from 'underscore';
import { getStyle } from './style';
import { ColumnChooser } from './BackboneWrapper';

interface ReplaceDialogProps {
  hidden: boolean;
  i18n: I18n;
  columnSetName: string;
  onDismiss: () => void;
  onReplace: () => void;
}

const ReplaceDialog: FC<ReplaceDialogProps> = (props) => {
  const {
    hidden,
    columnSetName,
    onReplace,
    onDismiss,
    i18n,
  } = props;

  const dialogContentProps = {
    type: DialogType.normal,
    closeButtonAriaLabel: i18n.getString(_TL_('Close')),
  };

  return (
    <Dialog
      hidden={hidden}
      onDismiss={onDismiss}
      dialogContentProps={dialogContentProps}
    >
      <p>
        {i18n.getString(_TL_('There is already a set of columns with this name: {{savedColumnSetName}}'), { savedColumnSetName: columnSetName })}
      </p>
      <p>
        {i18n.getString(_TL_('Do you want to replace it?'))}
      </p>
      <DialogFooter>
        <PrimaryButton onClick={onReplace} text="Replace" />
        <DefaultButton onClick={onDismiss} text="Cancel" />
      </DialogFooter>
    </Dialog>);
};

interface OnApplyPayload {
  /**
   * selected column keys.
   */
  selectedKeys: string[],
  /**
   * Name of saved column set.
   */
  savedName: string,
  // only avaliable when simplified
  // for now this component is designed only for toolbar column chooser
  // These property should always be undefined
  // Keep them only for completion
  searchStr?: string,
  currentActive?: string
}

export interface ToolbarColumnChooserProps {
  legacyI18n: I18n,
  classes?: any,
  categories: ColumnChooserCategory[],
  // errorMessages, // When and what?
  // the selected column that is initialized, the changing state is stored and changed in the backbone Component.
  selected: string[],
  onApply: (payload: OnApplyPayload) => void;
  onCancel: () => void;
  /**
   * Preference related.
   *
   * Important to make sure you are using the same unique grid name for one grid.
   */
  gridName: string;
}

const ToolbarColumnChooser: FC<ToolbarColumnChooserProps> = (props) => {
  const {
    categories, legacyI18n, selected, onApply, onCancel, classes, gridName
  } = props;
  const instrumentation = useContext(InstrumentationContext);

  const [isDialogOpened, setIsDialogOpened] = useState(false);

  const applyValue = useRef<OnApplyPayload | undefined>(undefined);

  const handleApply: typeof onApply = (params) => {
    const query = {
      project: 'CampaignUI',
      grid: gridName,
    };

    const isDuplicatedColumnSetName = _.find(Columns.filter({ where: query }), _.matcher({ name: params.savedName }));

    if (isDuplicatedColumnSetName) {
      applyValue.current = params;
      setIsDialogOpened(true);
      return;
    }
    onApply(params);
  }

  const onReplace: ReplaceDialogProps["onReplace"] = () => {
    setIsDialogOpened(false);
    onApply(applyValue.current);
  }

  const onDismiss: ReplaceDialogProps["onDismiss"] = () => {
    setIsDialogOpened(false);
  }

  return (
    <div className={classes.columnChooser}>
      <ColumnChooser
        onApply={handleApply}
        onCancel={onCancel}
        instrumentation={instrumentation}
        categories={categories}
        legacyI18n={legacyI18n}
        selected={selected}
        isSplittingSelectedArea={false}
        simplified={false}
      />
      <ReplaceDialog
        hidden={!isDialogOpened}
        columnSetName={applyValue.current?.savedName}
        i18n={legacyI18n}
        onDismiss={onDismiss}
        onReplace={onReplace}
      />
    </div>
  );
};

export const ToolbarColumnChooserWrapper: typeof ToolbarColumnChooser = withDefaultStyles(ToolbarColumnChooser, getStyle);
